import { GaugeIcon, MinusIcon } from '@phosphor-icons/react'

import { currencyMask } from '@utils/masks/currencyMask'
import { emotionTheme } from '@styles/theme'
import { TrendingDown } from '@assets/svgs/TrendingDown'
import { TrendingUp } from '@assets/svgs/TrendingUp'

import type { ColumnDefProps } from '@components/shared/Table/types'
import { findAccountConfig } from '@pages/ConsolidatedEntry/constants/accountsConfig'
import { useConsolidatedEntry } from '@pages/ConsolidatedEntry/context'
import { getWeekStatusLabel } from '@pages/ConsolidatedEntry/helpers/getWeekStatusLabel'
import { BudgetPreviewsStatus } from '@components/shared/BudgetPreviewsStatus'

import type { PreviewsStatusProps } from '@components/shared/BudgetPreviewsStatus/types'
import { getAccountBudgetPercentage } from './getAccountBudgetPercentage'
import { getAccountIcon } from './getAccountIcon'
import { shouldShowAccountPercentage } from './shouldShowAccountPercentage'

import type { PreviaSemanalDeviationColor, PreviaSemanalDeviationDirection } from '@services/api/consolidatedEntry/types'

import * as S from '../styles'

const CURRENT_WEEK_HEADER_COLOR = '#B0AFFAD9'
const CURRENT_WEEK_BODY_COLOR = '#B0AFFA59'

const TREND_ICON: Record<PreviaSemanalDeviationDirection, React.ReactNode> = {
  up: <TrendingUp size="small" />,
  down: <TrendingDown size="small" />,
  neutral: <MinusIcon size={12} weight="bold" />
}

const TREND_COLOR: Record<PreviaSemanalDeviationColor, string> = {
  up: emotionTheme.colors.success.color,
  down: emotionTheme.colors.error.color,
  neutral: emotionTheme.colors.gray[400]
}

type TrendDisplay = 'full' | 'iconOnly' | 'none'

const formatPercentage = (value: number) => `${value.toFixed(2)}%`.replace('.', ',')

const getTrendIcon = (direction: PreviaSemanalDeviationDirection) => TREND_ICON[direction] ?? TREND_ICON.neutral

const getTrendColor = (color: PreviaSemanalDeviationColor) => TREND_COLOR[color] ?? TREND_COLOR.neutral

const renderTrendValue = (
  value: number,
  direction: PreviaSemanalDeviationDirection,
  color: PreviaSemanalDeviationColor,
  percentage: number,
  trendDisplay: TrendDisplay = 'full'
) => (
  <S.CellWrapper>
    <S.CellValue>{currencyMask(value)}</S.CellValue>
    {trendDisplay !== 'none' && (
      <S.CellTrend>
        {trendDisplay === 'full' && formatPercentage(percentage)}
        <S.IconWrapper color={getTrendColor(color)}>{getTrendIcon(direction)}</S.IconWrapper>
      </S.CellTrend>
    )}
  </S.CellWrapper>
)

const renderPercentageValue = (percentage: number) => (
  <S.CellWrapper>
    <S.CellValue>{formatPercentage(percentage)}</S.CellValue>
  </S.CellWrapper>
)

const renderDeviationValue = (
  value: number,
  percentage: number,
  direction: PreviaSemanalDeviationDirection,
  color: PreviaSemanalDeviationColor,
  trendDisplay: TrendDisplay = 'full'
) => (
  <S.DeviationWrapper>
    <S.DeviationValue>{currencyMask(value)}</S.DeviationValue>
    {trendDisplay !== 'none' && (
      <S.DeviationPill>
        {trendDisplay === 'full' && formatPercentage(percentage)}
        <S.IconWrapper color={getTrendColor(color)}>{getTrendIcon(direction)}</S.IconWrapper>
      </S.DeviationPill>
    )}
  </S.DeviationWrapper>
)

const renderWeekHeader = (weekNumber: number, status: string) => (
  <S.WeekHeader>
    <span>{`Semana ${weekNumber}`}</span>
    <S.WeekHeaderStatus>
      <BudgetPreviewsStatus
        compact
        status={{ code: status as PreviewsStatusProps, descriptionPtbr: getWeekStatusLabel(status) }}
      />
    </S.WeekHeaderStatus>
  </S.WeekHeader>
)

export const parseConsolidatedGrid = () => {
  const { consolidatedData } = useConsolidatedEntry()

  const weeks = consolidatedData?.weeks ?? []
  const accounts = consolidatedData?.accounts ?? []
  const invoicingBudget =
    accounts.find((account) => findAccountConfig(account.account)?.key === 'invoicing')?.budget ?? 0
  const deviationColumnIndex = 2 + weeks.length

  const columns: ColumnDefProps[] = [
    {
      id: 'account',
      columnName: 'Conta',
      value: '',
      headerAlign: 'center',
      bodyAlign: 'left',
      minWidth: '12rem'
    },
    { id: 'budget', columnName: 'Budget', value: '', headerAlign: 'center', bodyAlign: 'right', minWidth: '9rem' },
    ...weeks.map((week) => ({
      id: `week-${week.weekNumber}`,
      columnName: renderWeekHeader(week.weekNumber, week.status),
      value: '',
      headerAlign: 'center' as const,
      bodyAlign: 'right' as const,
      minWidth: '9rem',
      columnColor: week.isCurrent ? CURRENT_WEEK_HEADER_COLOR : undefined,
      bodyColumnColor: week.isCurrent ? CURRENT_WEEK_BODY_COLOR : undefined
    })),
    { id: 'deviation', columnName: 'Desvio vs Budget', value: '', headerAlign: 'center', bodyAlign: 'right', minWidth: '10rem' }
  ]

  const data: ColumnDefProps[][] = accounts.map((account) => {
    const { icon, color } = getAccountIcon(account.account)
    const budgetPercentage = getAccountBudgetPercentage(account.budget, invoicingBudget)
    const showPercentage = shouldShowAccountPercentage(account.account)
    const weekTrendDisplay: TrendDisplay = showPercentage ? 'full' : 'iconOnly'

    return [
      {
        ...columns[0],
        value: (
          <S.RowLabel>
            <S.AccountIconWrapper color={color}>{icon}</S.AccountIconWrapper>
            {account.account}
          </S.RowLabel>
        ),
        withoutTooltip: true
      },
      {
        ...columns[1],
        value: (
          <S.CellWrapper>
            <S.CellValue>{currencyMask(account.budget)}</S.CellValue>
            {showPercentage && <S.CellTrend>{formatPercentage(budgetPercentage)}</S.CellTrend>}
          </S.CellWrapper>
        ),
        withoutTooltip: true
      },
      ...weeks.map((week, index) => {
        const weekValue = account.weeks.find((accountWeek) => accountWeek.weekNumber === week.weekNumber)

        return {
          ...columns[2 + index],
          value: weekValue
            ? renderTrendValue(
                weekValue.value,
                weekValue.evolutionDirection,
                weekValue.evolutionColor,
                weekValue.deviationVsBudgetPercentage,
                weekTrendDisplay
              )
            : null,
          withoutTooltip: true
        }
      }),
      {
        ...columns[deviationColumnIndex],
        value: renderDeviationValue(
          account.deviationVsBudgetAmount,
          account.deviationVsBudgetPercentage,
          account.deviationDirection,
          account.deviationColor,
          weekTrendDisplay
        ),
        withoutTooltip: true
      }
    ]
  })

  const grossProfitRow: ColumnDefProps[] = consolidatedData
    ? [
        { ...columns[0], value: 'Gross Profit (GP)', fontWeight: 600, withoutTooltip: true },
        {
          ...columns[1],
          value: <S.CellValue>{currencyMask(consolidatedData.grossProfit.budget)}</S.CellValue>,
          fontWeight: 600,
          withoutTooltip: true
        },
        ...weeks.map((week, index) => {
          const weekValue = consolidatedData.grossProfit.weeks.find(
            (grossWeek) => grossWeek.weekNumber === week.weekNumber
          )

          return {
            ...columns[2 + index],
            value: weekValue
              ? renderTrendValue(
                  weekValue.value,
                  weekValue.evolutionDirection,
                  weekValue.evolutionColor,
                  weekValue.deviationVsBudgetPercentage,
                  'none'
                )
              : null,
            fontWeight: 600,
            withoutTooltip: true
          }
        }),
        {
          ...columns[deviationColumnIndex],
          value: renderDeviationValue(
            consolidatedData.grossProfit.deviationVsBudgetAmount,
            consolidatedData.grossProfit.deviationVsBudgetPercentage,
            consolidatedData.grossProfit.deviationDirection,
            consolidatedData.grossProfit.deviationColor,
            'none'
          ),
          fontWeight: 600,
          withoutTooltip: true
        }
      ]
    : []

  const grossMarginRow: ColumnDefProps[] = consolidatedData
    ? [
        {
          ...columns[0],
          value: (
            <S.GmLabel>
              <GaugeIcon size={18} />
              Gross Margin (GM)
            </S.GmLabel>
          ),
          fontWeight: 600,
          columnColor: emotionTheme.colors.blue.head,
          bodyColumnColor: emotionTheme.colors.blue.head,
          withoutTooltip: true
        },
        {
          ...columns[1],
          value: renderPercentageValue(consolidatedData.grossMargin.budget),
          fontWeight: 600,
          columnColor: emotionTheme.colors.blue.head,
          bodyColumnColor: emotionTheme.colors.blue.head,
          withoutTooltip: true
        },
        ...weeks.map((week, index) => {
          const weekValue = consolidatedData.grossMargin.weeks.find(
            (marginWeek) => marginWeek.weekNumber === week.weekNumber
          )

          return {
            ...columns[2 + index],
            value: renderPercentageValue(weekValue?.value ?? 0),
            fontWeight: 600,
            columnColor: emotionTheme.colors.blue.head,
            bodyColumnColor: emotionTheme.colors.blue.head,
            withoutTooltip: true
          }
        }),
        {
          ...columns[deviationColumnIndex],
          value: renderPercentageValue(consolidatedData.grossMargin.deviationVsBudgetPercentage),
          fontWeight: 600,
          columnColor: emotionTheme.colors.blue.head,
          bodyColumnColor: emotionTheme.colors.blue.head,
          withoutTooltip: true
        }
      ]
    : []

  return { data, totalData: [grossProfitRow, grossMarginRow].filter((row) => row.length > 0) }
}
